import { Alert, DialogContent } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { GqlJob } from "~/__generated__";
import WorkerJobStatus from "~/modules/app/components/WorkerJobStatus";
import FileUploaderStatus from "~/modules/media/components/FileUploaderStatus";

import useMapEditorLayerCreateFromFile from "../../api/useMapEditorLayerCreateFromFile";

type LayerImportJobOutput = { type: "success"; layerId: string } | { type: "failed"; errorMessage: string };

type Props = {
    projectId: string;
    onClose: () => void;
    onFinished: (layerId: string) => void;
};

export default function MapEditorLayerImport({ projectId, onClose, onFinished }: Props) {
    const { t } = useTranslation("mapEditor");
    const { layerCreate, loading, data } = useMapEditorLayerCreateFromFile();

    const handleFinished = useCallback(
        (job: GqlJob) => {
            const output = job.output as LayerImportJobOutput | null | undefined;

            // pg-boss wraps worker errors and returns { type: "failed", errorMessage }
            // with status COMPLETED (see pgBoss.config.ts createProcessorHandler),
            if (output?.type === "failed") {
                // Keep the dialog open so the error message stays visible.
                return;
            }

            if (output?.type === "success" && output.layerId) {
                onFinished(output.layerId);
            } else {
                onClose();
            }
        },
        [onClose, onFinished],
    );

    return (
        <DialogContent>
            {data && (
                <WorkerJobStatus
                    worker={data.worker}
                    jobId={data.id}
                    onFinished={handleFinished}
                    renderFeedback={job => {
                        const output = job.output as LayerImportJobOutput | null | undefined;
                        if (output?.type === "failed") {
                            return (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {t("mapEditorLayerImport.couldNotImport")}
                                </Alert>
                            );
                        }

                        return <span />;
                    }}
                />
            )}
            {!data && !loading && (
                <FileUploaderStatus
                    sx={{ width: "100%" }}
                    multiple={false}
                    fileTypesToAccept={{ "application/geo+json": [".geojson"], "application/json": [".json"] }}
                    onUploadFinished={media => {
                        layerCreate(projectId, media[0].id);
                    }}
                />
            )}
        </DialogContent>
    );
}
